import { useState } from 'react';
import { DollarSign, Gift, Coins, TrendingUp, Zap } from 'lucide-react';

const BonusCalculator = () => {
  const [depositAmount, setDepositAmount] = useState(35);
  
  const COIN_VALUE = 0.70;
  const MAX_BONUS_COINS = 100;
  const MIN_DEPOSIT_FOR_BONUS = 30;
  
  const depositCoins = depositAmount / COIN_VALUE;
  const isBonusActive = depositAmount >= MIN_DEPOSIT_FOR_BONUS;
  const bonusCoins = isBonusActive ? Math.min(depositCoins, MAX_BONUS_COINS) : 0;
  const bonusUSD = bonusCoins * COIN_VALUE;
  const totalCoins = depositCoins + bonusCoins;
  const totalUSD = depositAmount + bonusUSD;
  const bonusPercentage = isBonusActive ? (bonusCoins / depositCoins) * 100 : 0;

  const formatNumber = (num) => num.toFixed(2);

  return (
    <div className="w-full max-w-xl bg-csgo-surface/80 backdrop-blur-md border border-csgo-border rounded-2xl p-4 sm:p-6 md:p-8 neon-border" data-testid="bonus-calculator">
      <h3 className="font-orbitron font-bold text-base sm:text-xl md:text-2xl text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-csgo-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
          <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-csgo-primary" />
        </div>
        <span className="leading-tight">CSGOLuck Bonus Calculator</span>
      </h3>

      {/* Deposit Amount Slider */}
      <div className="mb-6 sm:mb-8">
        <div className="flex justify-between items-center mb-3">
          <label className="font-rajdhani font-semibold text-csgo-textMuted uppercase tracking-wider text-xs sm:text-sm">
            Your Deposit
          </label>
          <div className="flex items-center gap-1 bg-csgo-bg px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg border border-csgo-border">
            <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-csgo-primary" />
            <span className="font-orbitron font-bold text-lg sm:text-xl text-white" data-testid="deposit-display">
              {depositAmount}
            </span>
          </div>
        </div>
        
        <div className="relative">
          <input
            type="range"
            min="1"
            max="100"
            value={depositAmount}
            onChange={(e) => setDepositAmount(Number(e.target.value))}
            className="w-full h-2 cursor-pointer"
            data-testid="deposit-slider"
          />
          
          <div 
            className="absolute top-0 flex flex-col items-center pointer-events-none"
            style={{ left: 'calc(29% - 12px)' }}
          >
            <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center -mt-[7px] sm:-mt-[9px] transition-all duration-300 ${
              depositAmount >= MIN_DEPOSIT_FOR_BONUS 
                ? 'bg-csgo-primary border-csgo-primary' 
                : 'bg-csgo-surface border-csgo-border'
            }`}>
              <Zap className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${depositAmount >= MIN_DEPOSIT_FOR_BONUS ? 'text-black' : 'text-csgo-textMuted'}`} />
            </div>
          </div>
        </div>
        
        <div className="flex justify-between mt-3 sm:mt-4 text-xs text-csgo-textMuted font-rajdhani">
          <span>$1</span>
          <span className={`font-semibold text-center ${depositAmount >= MIN_DEPOSIT_FOR_BONUS ? 'text-csgo-primary' : 'text-csgo-textMuted'}`}>
            $30 Bonus
          </span>
          <span>$100</span>
        </div>

        {!isBonusActive && (
          <div className="mt-3 bg-csgo-secondary/10 border border-csgo-secondary/30 rounded-lg px-3 py-2 text-center">
            <p className="font-rajdhani text-xs sm:text-sm text-csgo-secondary">
              <Zap className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
              Deposit $30+ to activate the bonus!
            </p>
          </div>
        )}
      </div>

      {/* Results Grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-csgo-bg p-3 sm:p-4 rounded-xl border border-csgo-border">
          <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
            <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-csgo-textMuted" />
            <span className="font-rajdhani text-xs sm:text-sm text-csgo-textMuted uppercase">Deposit</span>
          </div>
          <p className="font-orbitron font-bold text-lg sm:text-2xl text-white" data-testid="deposit-coins">
            {formatNumber(depositCoins)} <span className="text-xs sm:text-sm text-csgo-textMuted">coins</span>
          </p>
          <p className="font-rajdhani text-xs sm:text-sm text-csgo-textMuted">${depositAmount}</p>
        </div>

        <div className={`p-3 sm:p-4 rounded-xl border transition-all duration-300 ${
          isBonusActive 
            ? 'bg-csgo-primary/10 border-csgo-primary/30' 
            : 'bg-csgo-bg border-csgo-border opacity-60'
        }`}>
          <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
            <Gift className={`w-3 h-3 sm:w-4 sm:h-4 ${isBonusActive ? 'text-csgo-primary' : 'text-csgo-textMuted'}`} />
            <span className={`font-rajdhani text-xs sm:text-sm uppercase ${isBonusActive ? 'text-csgo-primary' : 'text-csgo-textMuted'}`}>
              Bonus {!isBonusActive && '(Off)'}
            </span>
          </div>
          <p className={`font-orbitron font-bold text-lg sm:text-2xl ${isBonusActive ? 'text-csgo-primary neon-text' : 'text-csgo-textMuted'}`} data-testid="bonus-coins">
            +{formatNumber(bonusCoins)} <span className="text-xs sm:text-sm">coins</span>
          </p>
          <p className={`font-rajdhani text-xs sm:text-sm ${isBonusActive ? 'text-csgo-primary/70' : 'text-csgo-textMuted'}`}>
            +${formatNumber(bonusUSD)}
          </p>
        </div>
      </div>

      {/* Total Playable */}
      <div className="bg-gradient-to-r from-csgo-primary/20 to-csgo-secondary/20 p-4 sm:p-5 rounded-xl border border-csgo-primary/40 mb-4 sm:mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
              <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-csgo-secondary" />
              <span className="font-rajdhani font-semibold text-csgo-textMuted uppercase text-xs sm:text-sm">Total Playable</span>
            </div>
            <p className="font-orbitron font-black text-2xl sm:text-3xl md:text-4xl text-white" data-testid="total-coins">
              {formatNumber(totalCoins)} <span className="text-sm sm:text-lg text-csgo-secondary">coins</span>
            </p>
            <p className="font-rajdhani text-sm sm:text-lg text-csgo-textMuted">${formatNumber(totalUSD)} value</p>
          </div>
          <div className="text-right">
            <div className={`px-2 sm:px-3 py-1 rounded-full ${isBonusActive ? 'bg-csgo-primary' : 'bg-csgo-border'}`}>
              <span className={`font-orbitron font-bold text-xs sm:text-sm ${isBonusActive ? 'text-black' : 'text-csgo-textMuted'}`}>
                {isBonusActive ? (bonusPercentage >= 100 ? '100%' : `${Math.round(bonusPercentage)}%`) : '0%'} BONUS
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Button */}
      <a
        href="https://csgoluck.com/r/CS2026"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="calculator-claim-btn"
        className="w-full flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-csgo-primary text-black font-orbitron font-black text-sm sm:text-lg uppercase tracking-wider rounded-xl hover:bg-csgo-primary/90 transition-all duration-200 neon-glow hover:scale-[1.02]"
      >
        Claim Your ${formatNumber(bonusUSD)} Bonus
      </a>

      <p className="text-center text-xs text-csgo-textMuted mt-3 sm:mt-4 font-rajdhani">
        Use code <span className="text-csgo-primary font-bold">CS2026</span> at checkout
      </p>
    </div>
  );
};

export default BonusCalculator;
